import { useEffect, useState } from 'react';
import HoleScreen from './screens/HoleScreen';
import { loadCourse } from './lib/courses';
import { useGps } from './lib/gps';
import { fetchWeather } from './lib/weather';
import { useSettings } from './lib/settings';
import type { Course, Weather } from './lib/types';

/**
 * Host for the vertical hole surface.
 *
 * Kept separate from the map app so the CarPlay window can mount the hole and
 * nothing else: the entitlement rules make "what is on the car screen" a
 * property of the route, and a route that can only ever draw a hole is far
 * easier to keep compliant than one that could grow a scorecard by accident.
 *
 * `?course=` and `?hole=` exist so a screenshot or a demo lands on an exact
 * hole without tapping through the app.
 */
export default function HoleApp() {
  const q = new URLSearchParams(location.search);
  const { settings } = useSettings();
  const [course, setCourse] = useState<Course | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [hole, setHole] = useState<number>(Number(q.get('hole')) || 1);
  const [weather, setWeather] = useState<Weather | null>(null);

  // A demo fix keeps the blue dot and the "from where you stand" distances
  // truthful in screenshots, where there is no GPS to be had.
  const demo = q.get('at');
  const { fix } = useGps({
    simulate: demo ? (demo.split(',').map(Number) as [number, number]) : null,
  });

  const courseId = q.get('course') ?? settings.courseId ?? 'way/43527178';

  useEffect(() => {
    let alive = true;
    loadCourse(courseId)
      .then((r) => { if (alive) setCourse(r.course); })
      .catch((e) => { if (alive) setFailed(String(e)); });
    return () => { alive = false; };
  }, [courseId]);

  // Wind and temperature are what turn a distance into a club, so the caddy
  // rail is worth a network call the moment we know where the course is.
  useEffect(() => {
    if (!course) return;
    let alive = true;
    fetchWeather(course.centre).then((w) => { if (alive) setWeather(w); }).catch(() => {});
    return () => { alive = false; };
  }, [course]);

  if (failed) return <div className="hole-surface"><div className="hs-empty">{failed}</div></div>;
  if (!course) return <div className="hole-surface"><div className="hs-empty">Loading…</div></div>;

  return (
    <HoleScreen
      course={course}
      holeNumber={hole}
      teeColour={settings.teeColour ?? course.teeColours[0]}
      position={fix?.pos ?? null}
      weather={weather}
      unit={settings.unit ?? 'm'}
      onHoleChange={setHole}
    />
  );
}
